import { Box } from '@mui/material';
import { SelectAdSettings, SelectCallToActions, SelectConversionDomain } from './FacebookFormComponents';
import type { FC } from 'react';
import { Suspense, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { SuspenseErrorFallback } from 'components/common';
import { Platform } from '../../../constants';
import { ErrorBoundary } from 'react-error-boundary';
import type { PackEntry } from 'types/shared';
import { SINGLE_AD_TYPE } from '../constants';
import { PartnershipCreationMode, PartnershipIdentityToShow } from '../types';
import {
  CreateAdsHeader,
  FormFieldSkeleton,
  HotjarSection,
  PartnershipAdsRestrictionAlert,
  PartnershipAdsSwitcherRow,
  PartnershipsSectionSuspense,
} from './AdFormComponents';
import { createDefaultPartnershipAdSetting } from '../utils/partnershipAdSettings';
import { isScalingEgProcess } from '../utils/marketingProcess';
import queryKeys from '../api/queryKeys';

const AdForm: FC = () => {
  const queryClient = useQueryClient();
  const { watch, setValue } = useFormContext();
  const prevPartnershipAdsEnabledRef = useRef<boolean | null>(null);
  const marketingProcess = watch('marketingProcess') as string;
  const isMarketingProcessScalingEg = isScalingEgProcess(marketingProcess);
  const platform = watch('platform') as string;
  const partnershipAdsEnabled = watch('partnershipAdsEnabled') as boolean;
  const packsWithCreatives = watch('packsWithCreatives') as PackEntry[] | undefined;
  const isWebPlatform = String(platform) === String(Platform.WEB);
  const allPacksAreSingleAd = useMemo(() => {
    if (!packsWithCreatives?.length) {
      return false;
    }
    return packsWithCreatives.every((pack) => pack.adType === SINGLE_AD_TYPE);
  }, [packsWithCreatives]);
  const isPartnershipFormVisible = partnershipAdsEnabled && allPacksAreSingleAd;
  const isPartnershipRestrictionVisible = partnershipAdsEnabled && !allPacksAreSingleAd;

  useEffect(() => {
    const wasPartnershipAdsEnabled = prevPartnershipAdsEnabledRef.current;
    prevPartnershipAdsEnabledRef.current = partnershipAdsEnabled;

    if (wasPartnershipAdsEnabled && !partnershipAdsEnabled) {
      setValue('partnershipAdSettings', [createDefaultPartnershipAdSetting()], {
        shouldValidate: true,
      });
      setValue('selectedAdSettings', [{}], { shouldValidate: false });
      setValue('partnershipIsPartnerIdentityFirst', undefined, { shouldValidate: true });
      setValue('partnershipIdentityToShow', undefined, { shouldValidate: true });
      setValue('partnershipCreationMode', undefined, { shouldValidate: true });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adSettings });
      void queryClient.invalidateQueries({ queryKey: queryKeys.partnershipAdSettings });
      return;
    }

    if (
      !wasPartnershipAdsEnabled &&
      partnershipAdsEnabled &&
      isPartnershipFormVisible
    ) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.partnershipAdSettings });
      setValue('partnershipIsPartnerIdentityFirst', true, { shouldValidate: true });
      setValue('partnershipIdentityToShow', PartnershipIdentityToShow.BOTH, { shouldValidate: true });
      setValue('partnershipCreationMode', PartnershipCreationMode.PARTNERSHIP_ONLY, { shouldValidate: true });
    }
  }, [isPartnershipFormVisible, partnershipAdsEnabled, queryClient, setValue]);

  return (
    <>
      <CreateAdsHeader />
      <Box sx={{ display: 'flex', gap: 4, flexDirection: 'column', width: '100%' }}>
        <Box sx={{ display: 'flex', gap: 4, flexDirection: 'column', width: '50%' }}>
          <SelectCallToActions />
          {isWebPlatform && (
            <ErrorBoundary
              fallback={
                <SuspenseErrorFallback title="Conversion Domain" message="Error occur" />
              }
            >
              <Suspense fallback={<FormFieldSkeleton />}>
                <SelectConversionDomain />
              </Suspense>
            </ErrorBoundary>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 3, maxWidth: '50%', flexDirection: 'column', width: '100%', mb: 2 }}>
          {!isPartnershipFormVisible && (
            <ErrorBoundary
              fallback={
                <SuspenseErrorFallback title="Ad Settings" message="Error occur" />
              }
            >
              <SelectAdSettings />
            </ErrorBoundary>
          )}
          {!isMarketingProcessScalingEg && <PartnershipAdsSwitcherRow />}
          {isPartnershipFormVisible && <PartnershipsSectionSuspense />}
          {isPartnershipRestrictionVisible && <PartnershipAdsRestrictionAlert />}
          {isWebPlatform && <HotjarSection />}
        </Box>
      </Box>
    </>
  );
};

export default AdForm;
